import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  IconButton,
  Divider,
  Snackbar,
  Alert,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { IDeliveryListDTO, IPincodeAssignmentResponseDTO, IAssignOrdersResponseDTO } from "../../interfaces/Delivery";
import { availablePartners, bulkAssign } from "../../services/Delivery";
import { useAuth } from "../../auth/AuthProvider";

interface BulkAssignDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  selectedDeliveries: IDeliveryListDTO[];
}

const BulkAssignDialog = ({ open, onClose, onSave, selectedDeliveries }: BulkAssignDialogProps) => {
  const { getLoginUserInfo } = useAuth();
  const loggedInUser = getLoginUserInfo();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [assignmentData, setAssignmentData] = useState<IPincodeAssignmentResponseDTO | null>(null);
  const [assignmentResults, setAssignmentResults] = useState<IAssignOrdersResponseDTO[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [msg, setMsg] = useState("");
  const [openError, setOpenError] = useState(false);
  const [openSuccess, setOpenSuccess] = useState(false);

  useEffect(() => {
    if (!open || !loggedInUser?.companyId || selectedDeliveries.length === 0) return;
    (async () => {
      try {
        setLoading(true);
        const orderIds = selectedDeliveries.map(d => d.orderId);
        const res = await availablePartners(loggedInUser.companyId!, orderIds);
        if (res?.status === "success") {
          setAssignmentData(res.rawResponse?.data as IPincodeAssignmentResponseDTO);
        }
      } catch {
        setAssignmentData(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  const handleAssign = async () => {
    if (!loggedInUser?.companyId|| selectedDeliveries.length === 0) return;
    setSaving(true);
    try {
      const res = await bulkAssign({ orderIds: selectedDeliveries.map(d => d.orderId), companyId: loggedInUser.companyId! });
      if (res?.status === "success") {
        const data = res.rawResponse?.data;
        setAssignmentResults(Array.isArray(data) ? data : []);
        setShowResults(true);
        setOpenSuccess(true);
        setMsg("Orders assigned successfully.");
      } else {
        setOpenError(true);
        setMsg("Failed to assign orders.");
      }
    } catch {
      setOpenError(true);
      setMsg("Error assigning orders.");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (showResults) onSave();
    setAssignmentData(null);
    setAssignmentResults([]);
    setShowResults(false);
    onClose();
  };

  const handleSnackbarClose = () => {
    setOpenError(false);
    setOpenSuccess(false);
    setMsg("");
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{ sx: { width: "550px", maxWidth: "550px" } }}
      slotProps={{ backdrop: { sx: { backdropFilter: "blur(4px)" } } }}
    >
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 0 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {showResults ? "Package assignments" : "Bulk Assignment"}
          </Typography>
          <Typography variant="body2" sx={{ color: "grey.700", fontSize: "12px" }}>
            {showResults ? "Following packages are auto assigned to delivery partner based on the pincodes." : "Assign Packages to delivery partner."}
          </Typography>
        </Box>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : showResults ? (
          <Paper sx={{ border: "1px solid", borderColor: "grey.100", maxHeight: 300, overflow: "hidden", mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: "grey.100", position: "sticky", top: 0, zIndex: 1 }}>Partner ID</TableCell>
                  <TableCell sx={{ bgcolor: "grey.100", position: "sticky", top: 0, zIndex: 1 }}>Partner Name</TableCell>
                  <TableCell sx={{ bgcolor: "grey.100", position: "sticky", top: 0, zIndex: 1 }}>Pincodes</TableCell>
                  <TableCell sx={{ bgcolor: "grey.100", position: "sticky", top: 0, zIndex: 1 }}>Orders</TableCell>
                </TableRow>
              </TableHead>
            </Table>
            <Box sx={{ maxHeight: 240, overflow: "auto", "&::-webkit-scrollbar": { width: "8px" }, "&::-webkit-scrollbar-track": { background: "transparent" }, "&::-webkit-scrollbar-thumb": { background: "#CBD5E1", borderRadius: "4px" } }}>
              <Table size="small">
                <TableBody>
                  {assignmentResults.map((result) => (
                    <TableRow key={result.deliveryPartnerId}>
                      <TableCell>{result.deliveryPartnerId}</TableCell>
                      <TableCell>{result.deliveryPartnerName}</TableCell>
                      <TableCell>
                        {result.assignedPincodes.length <= 2
                          ? result.assignedPincodes.join(", ")
                          : (
                            <Tooltip title={result.assignedPincodes.join(", ")} arrow>
                              <span>{result.assignedPincodes.slice(0, 2).join(", ")} +{result.assignedPincodes.length - 2} more</span>
                            </Tooltip>
                          )
                        }
                      </TableCell>
                      <TableCell>{result.assignedOrderCount}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Paper>
        ) : assignmentData ? (
          <Box>
            <Typography variant="h6" sx={{ mt: 2, fontSize: "16px", fontWeight: 600 }}>Assign to Partners</Typography>
            <Paper sx={{ border: "1px solid", borderColor: "grey.100", maxHeight: 300, overflow: "hidden" }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: "grey.100", position: "sticky", top: 0, zIndex: 1 }}>Pincodes</TableCell>
                    <TableCell sx={{ bgcolor: "grey.100", position: "sticky", top: 0, zIndex: 1 }}>Packages</TableCell>
                    <TableCell align="center" sx={{ bgcolor: "grey.100", position: "sticky", top: 0, zIndex: 1 }}>Delivery Partner</TableCell>
                  </TableRow>
                </TableHead>
              </Table>
              <Box sx={{ maxHeight: 240, overflow: "auto", "&::-webkit-scrollbar": { width: "8px" }, "&::-webkit-scrollbar-track": { background: "transparent" }, "&::-webkit-scrollbar-thumb": { background: "#CBD5E1", borderRadius: "4px" } }}>
                <Table>
                  <TableBody>
                    {assignmentData.pincodeOrders.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ width: "33.33%" }}>{item.pincode}</TableCell>
                        <TableCell sx={{ width: "33.33%", borderRight: "1px solid", borderColor: "grey.300" }}>{item.orderCount}</TableCell>
                        {index === 0 && (
                          <TableCell rowSpan={assignmentData.pincodeOrders.length} align="center" sx={{ width: "33.33%", color: "success.main", verticalAlign: "middle", fontWeight: 600 }}>
                            {assignmentData.availablePartnerCount} Available
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Paper>
          </Box>
        ) : (
          <Typography color="grey.500" sx={{ py: 4, textAlign: "center" }}>No assignment data available</Typography>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2, pt: 2 }}>
        {!showResults ? (
          <>
            <Button
              onClick={handleClose}
              sx={{ borderRadius: "6px", border: "1px solid", borderColor: "primary.main", boxShadow: "0 0 10.7px 0 rgba(0, 94, 255, 0.20) inset", padding: "8px 16px", textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={loading || saving || !assignmentData}
              onClick={handleAssign}
              sx={{ borderRadius: "6px", border: "1px solid", borderColor: "primary.main", boxShadow: "0 0 10.7px 0 rgba(0, 0, 0, 0.25) inset", padding: "8px 40px", textTransform: "none", "&:hover": { backgroundColor: "primary.main", boxShadow: "0 0 10.7px 0 rgba(0, 0, 0, 0.25) inset" } }}
            >
              {saving ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : "Assign"}
            </Button>
          </>
        ) : (
          <Button
            variant="contained"
            onClick={handleClose}
            sx={{ borderRadius: "6px", border: "1px solid", borderColor: "primary.main", boxShadow: "0 0 10.7px 0 rgba(0, 0, 0, 0.25) inset", padding: "8px 40px", textTransform: "none", ml: "auto", "&:hover": { backgroundColor: "primary.main", boxShadow: "0 0 10.7px 0 rgba(0, 0, 0, 0.25) inset" } }}
          >
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>

    <Snackbar anchorOrigin={{ vertical: "top", horizontal: "right" }} open={openError} autoHideDuration={5000} onClose={handleSnackbarClose}>
      <Alert severity="error" variant="filled" sx={{ width: "100%" }}>{msg}</Alert>
    </Snackbar>
    <Snackbar anchorOrigin={{ vertical: "top", horizontal: "right" }} open={openSuccess} autoHideDuration={5000} onClose={handleSnackbarClose}>
      <Alert severity="success" variant="filled" sx={{ width: "100%" }}>{msg}</Alert>
    </Snackbar>
    </>
  );
};
export default BulkAssignDialog;
